import React, { useState, useEffect, useCallback } from 'react';
import { Task, TaskFilter, TaskGroup, TaskStatus, BusinessUnit, UserRole, TaskDashboardStats } from '../types/task';
import { getTaskService } from '../services/sharepointHelpers';
import { getAuthService } from '../services/authService';
import TaskForm from './TaskForm';
import TaskTable from './TaskTable';
import TaskDetailsModal from './TaskDetailsModal';
import {
  Card,
  makeStyles,
  tokens,
  Button,
  Select,
  Input,
  Label,
  Text,
  Title3,
  Caption1,
  Spinner,
  MessageBar,
  Badge,
  TabList,
  Tab,
} from '@fluentui/react-components';
import {
  DocumentRegular,
  ClockRegular,
  AlertRegular,
  CheckmarkRegular,
  PersonRegular,
  SearchRegular,
  FilterRegular,
  AddRegular,
  BuildingRegular,
  CalendarRegular,
} from '@fluentui/react-icons';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: tokens.colorNeutralBackground1,
  },
  header: {
    padding: '24px',
    backgroundColor: tokens.colorNeutralBackground2,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  headerContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    maxWidth: '1400px',
    margin: '0 auto',
  },
  headerLeft: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  content: {
    flex: 1,
    padding: '24px',
    maxWidth: '1400px',
    margin: '0 auto',
    width: '100%',
  },
  quickFilters: {
    marginBottom: '24px',
    padding: '16px',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusLarge,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    boxShadow: tokens.shadow2,
  },
  quickFilterTabs: {
    marginBottom: '16px',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '16px',
    marginBottom: '24px',
    '@media (max-width: 768px)': {
      gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
      gap: '12px',
      marginBottom: '20px',
    },
  },
  statCard: {
    padding: '20px',
    borderRadius: '12px',
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    boxShadow: tokens.shadow2,
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    transition: 'all 0.2s',
    cursor: 'pointer',
    ':hover': {
      boxShadow: tokens.shadow8,
      transform: 'translateY(-4px)',
      border: `2px solid ${tokens.colorBrandStroke1}`,
    },
  },
  statCardActive: {
    border: `2px solid ${tokens.colorBrandStroke1}`,
    backgroundColor: tokens.colorBrandBackground2,
  },
  statHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  statIcon: {
    width: '40px',
    height: '40px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '20px',
    flexShrink: 0,
  },
  statContent: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
  statValue: {
    fontSize: '28px',
    fontWeight: tokens.fontWeightBold,
    color: tokens.colorNeutralForeground1,
    lineHeight: '1.2',
  },
  statLabel: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
    fontWeight: tokens.fontWeightMedium,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  filtersCard: {
    marginBottom: '24px',
  },
  filtersHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
  filtersContent: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '12px',
  },
  filterGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '80px 48px',
    textAlign: 'center',
    minHeight: '400px',
  },
  emptyStateIcon: {
    fontSize: '80px',
    marginBottom: '32px',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '400px',
  },
  departmentGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '16px',
    marginBottom: '24px',
  },
  departmentCard: {
    padding: '20px',
    borderRadius: '12px',
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    boxShadow: tokens.shadow2,
  },
  departmentHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
    paddingBottom: '12px',
    borderBottom: `2px solid ${tokens.colorNeutralStroke2}`,
  },
  departmentTitle: {
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightBold,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  statusBreakdown: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  statusRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 12px',
    borderRadius: '6px',
    backgroundColor: tokens.colorNeutralBackground2,
  },
});

type QuickFilter = 'my-tasks' | 'all-tasks' | 'by-department' | 'clinic-tasks';

interface TaskDashboardProps {
  userRole: UserRole;
  userId: string;
  userName: string;
}

const TaskDashboard: React.FC<TaskDashboardProps> = ({ userRole, userId, userName }) => {
  const styles = useStyles();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [filteredTasks, setFilteredTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [stats, setStats] = useState<TaskDashboardStats>({
    totalTasks: 0,
    openTasks: 0,
    inProgressTasks: 0,
    completedTasks: 0,
    urgentTasks: 0,
    overdueTasks: 0,
    myTasks: 0,
    assignedByMe: 0
  });
  const [filter, setFilter] = useState<TaskFilter>({});
  const [groupBy, setGroupBy] = useState<'status' | 'businessUnit' | 'assignedTo' | 'none'>('status');
  const [viewMode, setViewMode] = useState<'list' | 'kanban'>('list');
  const [quickFilter, setQuickFilter] = useState<QuickFilter>('my-tasks');
  const [activeStatFilter, setActiveStatFilter] = useState<string | null>(null);

  const taskService = getTaskService(getAuthService());

  // Load tasks based on user role
  const loadTasks = useCallback(async () => {
    try {
      console.log('🔍 TaskDashboard - loadTasks called');
      setLoading(true);
      setError(null);

      // For executives, load all tasks; for others, load only assigned tasks
      let roleFilter: any = {};
      
      if (userRole === UserRole.EXECUTIVE) {
        // Executives can see all tasks
      } else {
        // All other users can only see tasks assigned to them
        roleFilter.assignedTo = userName;
      }

      console.log('🔍 TaskDashboard - Getting tasks with filter:', roleFilter);
      const allTasks = await taskService.getTasks(roleFilter);
      console.log('🔍 TaskDashboard - Received tasks:', allTasks);
      setTasks(allTasks);

      // Load dashboard stats
      const dashboardStats = await taskService.getDashboardStats(userId, userRole, userName);
      setStats(dashboardStats);

    } catch (err) {
      console.error('Error loading tasks:', err);
      setError('Failed to load tasks. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [taskService, userRole, userName, userId]);

  // Apply filters and grouping
  const applyFilters = useCallback(() => {
    let filtered = [...tasks];

    // Apply quick filter
    if (quickFilter === 'my-tasks') {
      filtered = filtered.filter(task => 
        task.assignedTo.toLowerCase().includes(userName.toLowerCase())
      );
    } else if (quickFilter === 'clinic-tasks') {
      filtered = filtered.filter(task => 
        task.businessUnit === BusinessUnit.CLINICAL_OPERATIONS
      );
    } else if (quickFilter === 'by-department') {
      // Keep all tasks, will be grouped by department
    } else {
      // 'all-tasks' - show all
    }

    // Apply status filter
    if (filter.status && filter.status.length > 0) {
      filtered = filtered.filter(task => filter.status!.includes(task.status));
    }

    // Apply business unit filter
    if (filter.businessUnit && filter.businessUnit.length > 0) {
      filtered = filtered.filter(task => filter.businessUnit!.includes(task.businessUnit));
    }

    // Apply assigned to filter
    if (filter.assignedTo && filter.assignedTo.length > 0) {
      filtered = filtered.filter(task => filter.assignedTo!.includes(task.assignedTo));
    }

    // Apply urgent filter
    if (filter.urgent !== undefined) {
      filtered = filtered.filter(task => task.urgent === filter.urgent);
    }

    // Apply created by filter
    if (filter.createdBy && filter.createdBy.length > 0) {
      filtered = filtered.filter(task => filter.createdBy!.includes(task.createdBy));
    }

    // Apply date range filter
    if (filter.dateRange) {
      filtered = filtered.filter(task => {
        const taskDate = new Date(task.created);
        const startDate = new Date(filter.dateRange!.start);
        const endDate = new Date(filter.dateRange!.end);
        return taskDate >= startDate && taskDate <= endDate;
      });
    }

    // Apply active stat filter
    if (activeStatFilter) {
      switch (activeStatFilter) {
        case 'urgent':
          filtered = filtered.filter(task => task.urgent);
          break;
        case 'overdue':
          filtered = filtered.filter(task => {
            if (!task.dueDate) return false;
            return new Date(task.dueDate) < new Date() && task.status !== TaskStatus.COMPLETE;
          });
          break;
        case 'open':
          filtered = filtered.filter(task => task.status === TaskStatus.OPEN);
          break;
        case 'in-progress':
          filtered = filtered.filter(task => task.status === TaskStatus.IN_PROGRESS);
          break;
        case 'completed':
          filtered = filtered.filter(task => task.status === TaskStatus.COMPLETE);
          break;
        case 'my-tasks':
          filtered = filtered.filter(task => 
            task.assignedTo.toLowerCase().includes(userName.toLowerCase())
          );
          break;
      }
    }

    setFilteredTasks(filtered);
  }, [tasks, filter, quickFilter, activeStatFilter, userName]);

  // Group tasks for display
  const getGroupedTasks = (): TaskGroup[] => {
    if (quickFilter === 'by-department') {
      // Group by department, then by status
      const departmentGroups: { [key: string]: { [status: string]: Task[] } } = {};
      
      filteredTasks.forEach(task => {
        const dept = task.businessUnit;
        const status = task.status;
        
        if (!departmentGroups[dept]) {
          departmentGroups[dept] = {};
        }
        if (!departmentGroups[dept][status]) {
          departmentGroups[dept][status] = [];
        }
        departmentGroups[dept][status].push(task);
      });

      // Flatten into groups
      const groups: TaskGroup[] = [];
      Object.entries(departmentGroups).forEach(([dept, statusGroups]) => {
        Object.entries(statusGroups).forEach(([status, tasks]) => {
          groups.push({
            key: `${dept}-${status}`,
            label: `${dept} - ${status}`,
            tasks,
            count: tasks.length
          });
        });
      });

      return groups.sort((a, b) => {
        // Sort by department, then by status
        const deptA = a.label.split(' - ')[0];
        const deptB = b.label.split(' - ')[0];
        if (deptA !== deptB) return deptA.localeCompare(deptB);
        return a.label.localeCompare(b.label);
      });
    }

    if (groupBy === 'none') {
      return [{
        key: 'all',
        label: 'All Tasks',
        tasks: filteredTasks,
        count: filteredTasks.length
      }];
    }

    const groups: { [key: string]: Task[] } = {};

    filteredTasks.forEach(task => {
      let key = '';

      switch (groupBy) {
        case 'status':
          key = task.status;
          break;
        case 'businessUnit':
          key = task.businessUnit;
          break;
        case 'assignedTo':
          key = task.assignedTo;
          break;
      }

      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(task);
    });

    return Object.entries(groups).map(([key, tasks]) => ({
      key,
      label: key,
      tasks,
      count: tasks.length
    }));
  };

  // Get tasks by department breakdown
  const getDepartmentBreakdown = () => {
    const breakdown: { [dept: string]: { [status: string]: number } } = {};
    
    filteredTasks.forEach(task => {
      const dept = task.businessUnit;
      const status = task.status;
      
      if (!breakdown[dept]) {
        breakdown[dept] = {};
      }
      if (!breakdown[dept][status]) {
        breakdown[dept][status] = 0;
      }
      breakdown[dept][status]++;
    });

    return breakdown;
  };

  // Handle task creation
  const handleCreateTask = async (taskData: Omit<Task, 'id' | 'created' | 'createdBy'>) => {
    try {
      const newTask = await taskService.createTask(taskData);
      setTasks(prev => [newTask, ...prev]);
      setShowTaskForm(false);
      loadTasks(); // Refresh to get updated stats
    } catch (err) {
      console.error('Error creating task:', err);
      setError('Failed to create task. Please try again.');
    }
  };

  // Handle task update
  const handleUpdateTask = async (taskId: string, updates: Partial<Task>) => {
    try {
      console.log('🔍 TaskDashboard - handleUpdateTask called with:', { taskId, updates });
      await taskService.updateTask(taskId, updates);
      
      // Update local tasks state
      setTasks(prev => prev.map(task => 
        task.id === taskId ? { ...task, ...updates } : task
      ));
      
      // Update selectedTask if it's the one being updated
      setSelectedTask(prev => prev && prev.id === taskId ? { ...prev, ...updates } : prev);
      
      loadTasks(); // Refresh to get updated stats
    } catch (err) {
      console.error('Error updating task:', err);
      setError('Failed to update task. Please try again.');
    }
  };

  // Handle task deletion
  const handleDeleteTask = async (taskId: string) => {
    try {
      await taskService.deleteTask(taskId);
      setTasks(prev => prev.filter(task => task.id !== taskId));
      loadTasks(); // Refresh to get updated stats
    } catch (err) {
      console.error('Error deleting task:', err);
      setError('Failed to delete task. Please try again.');
    }
  };

  // Handle task selection for modal
  const handleTaskClick = (task: Task) => {
    console.log('🔍 TaskDashboard - Task clicked:', task);
    setSelectedTask(task);
    setShowTaskModal(true);
  };

  // Handle stat card click
  const handleStatClick = (statType: string) => {
    if (activeStatFilter === statType) {
      setActiveStatFilter(null);
    } else {
      setActiveStatFilter(statType);
      setQuickFilter('all-tasks'); // Switch to all tasks view when filtering by stat
    }
  };

  // Load tasks on component mount
  useEffect(() => {
    loadTasks();
  }, [userRole, userId, userName, loadTasks]);

  // Apply filters when filter state changes
  useEffect(() => {
    applyFilters();
  }, [tasks, filter, quickFilter, activeStatFilter, applyFilters]);

  // Check if there are no tasks to display
  const hasNoTasks = tasks.length === 0 || filteredTasks.length === 0;

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner size="large" label="Loading tasks..." />
      </div>
    );
  }

  // Show empty state if no tasks
  if (hasNoTasks && tasks.length === 0) {
    return (
      <div className={styles.root}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <div className={styles.headerLeft}>
              <Title3>Task Management</Title3>
              <Caption1>
                {userRole === UserRole.EXECUTIVE ? 'Manage all tasks across the organization' : 'Manage your assigned tasks'}
              </Caption1>
            </div>
            <div className={styles.headerRight}>
              <Button 
                appearance="primary" 
                icon={<AddRegular />}
                onClick={() => setShowTaskForm(true)}
              >
                Create Task
              </Button>
            </div>
          </div>
        </div>

        {/* Empty State */}
        <div className={styles.content}>
          <div className={styles.emptyState}>
            <div className={styles.emptyStateIcon}>📋</div>
            <Title3 style={{ marginBottom: '16px' }}>No tasks found</Title3>
            <Text style={{ marginBottom: '32px', fontSize: tokens.fontSizeBase400 }}>
              Get started by creating your first task.
            </Text>
            <Button 
              appearance="primary" 
              icon={<AddRegular />}
              onClick={() => setShowTaskForm(true)}
            >
              Create Your First Task
            </Button>
          </div>
        </div>

        {/* Task Form Modal */}
        {showTaskForm && (
          <TaskForm
            onSubmit={handleCreateTask}
            onCancel={() => setShowTaskForm(false)}
            userRole={userRole}
            userId={userId}
            userName={userName}
          />
        )}
      </div>
    );
  }

  const departmentBreakdown = getDepartmentBreakdown();

  return (
    <div className={styles.root}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <div className={styles.headerLeft}>
            <Title3>Task Management</Title3>
            <Caption1>
              {userRole === UserRole.EXECUTIVE ? 'Manage all tasks across the organization' : 'Manage your assigned tasks'}
            </Caption1>
          </div>
          <div className={styles.headerRight}>
            <Button 
              appearance="primary" 
              icon={<AddRegular />}
              onClick={() => setShowTaskForm(true)}
            >
              Create Task
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className={styles.content}>
        {/* Quick Filter Tabs */}
        <div className={styles.quickFilters}>
          <TabList
            selectedValue={quickFilter}
            onTabSelect={(_, data) => {
              setQuickFilter(data.value as QuickFilter);
              setActiveStatFilter(null); // Clear stat filter when switching tabs
            }}
            className={styles.quickFilterTabs}
          >
            <Tab value="my-tasks">
              My Tasks ({stats.myTasks})
            </Tab>
            <Tab value="all-tasks">
              All Tasks ({stats.totalTasks})
            </Tab>
            <Tab value="by-department">
              By Department
            </Tab>
            <Tab value="clinic-tasks">
              Clinic Tasks
            </Tab>
          </TabList>
        </div>

        {/* Stats Cards */}
        <div className={styles.statsGrid}>
          <div 
            className={`${styles.statCard} ${activeStatFilter === 'my-tasks' ? styles.statCardActive : ''}`}
            onClick={() => handleStatClick('my-tasks')}
          >
            <div className={styles.statHeader}>
              <div className={styles.statIcon} style={{ backgroundColor: tokens.colorPaletteGreenBackground2 }}>
                <PersonRegular />
              </div>
              <div className={styles.statContent}>
                <div className={styles.statValue}>{stats.myTasks}</div>
                <div className={styles.statLabel}>My Tasks</div>
              </div>
            </div>
          </div>

          <div 
            className={`${styles.statCard} ${activeStatFilter === 'urgent' ? styles.statCardActive : ''}`}
            onClick={() => handleStatClick('urgent')}
          >
            <div className={styles.statHeader}>
              <div className={styles.statIcon} style={{ backgroundColor: tokens.colorPaletteRedBackground2 }}>
                <AlertRegular />
              </div>
              <div className={styles.statContent}>
                <div className={styles.statValue}>{stats.urgentTasks}</div>
                <div className={styles.statLabel}>Urgent</div>
              </div>
            </div>
          </div>

          <div 
            className={`${styles.statCard} ${activeStatFilter === 'open' ? styles.statCardActive : ''}`}
            onClick={() => handleStatClick('open')}
          >
            <div className={styles.statHeader}>
              <div className={styles.statIcon} style={{ backgroundColor: tokens.colorPaletteYellowBackground2 }}>
                <ClockRegular />
              </div>
              <div className={styles.statContent}>
                <div className={styles.statValue}>{stats.openTasks}</div>
                <div className={styles.statLabel}>Open</div>
              </div>
            </div>
          </div>

          <div 
            className={`${styles.statCard} ${activeStatFilter === 'in-progress' ? styles.statCardActive : ''}`}
            onClick={() => handleStatClick('in-progress')}
          >
            <div className={styles.statHeader}>
              <div className={styles.statIcon} style={{ backgroundColor: tokens.colorPaletteBlueBackground2 }}>
                <DocumentRegular />
              </div>
              <div className={styles.statContent}>
                <div className={styles.statValue}>{stats.inProgressTasks}</div>
                <div className={styles.statLabel}>In Progress</div>
              </div>
            </div>
          </div>

          <div 
            className={`${styles.statCard} ${activeStatFilter === 'completed' ? styles.statCardActive : ''}`}
            onClick={() => handleStatClick('completed')}
          >
            <div className={styles.statHeader}>
              <div className={styles.statIcon} style={{ backgroundColor: tokens.colorPaletteGreenBackground2 }}>
                <CheckmarkRegular />
              </div>
              <div className={styles.statContent}>
                <div className={styles.statValue}>{stats.completedTasks}</div>
                <div className={styles.statLabel}>Completed</div>
              </div>
            </div>
          </div>

          <div 
            className={`${styles.statCard} ${activeStatFilter === 'overdue' ? styles.statCardActive : ''}`}
            onClick={() => handleStatClick('overdue')}
          >
            <div className={styles.statHeader}>
              <div className={styles.statIcon} style={{ backgroundColor: tokens.colorPaletteDarkOrangeBackground2 }}>
                <CalendarRegular />
              </div>
              <div className={styles.statContent}>
                <div className={styles.statValue}>{stats.overdueTasks}</div>
                <div className={styles.statLabel}>Overdue</div>
              </div>
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <MessageBar style={{ marginBottom: '16px' }}>
            {error}
          </MessageBar>
        )}

        {/* Department Breakdown View */}
        {quickFilter === 'by-department' && Object.keys(departmentBreakdown).length > 0 && (
          <div className={styles.departmentGrid}>
            {Object.entries(departmentBreakdown).map(([dept, statusCounts]) => {
              const totalForDept = Object.values(statusCounts).reduce((sum, count) => sum + count, 0);
              return (
                <Card key={dept} className={styles.departmentCard}>
                  <div className={styles.departmentHeader}>
                    <div className={styles.departmentTitle}>
                      <BuildingRegular />
                      {dept}
                    </div>
                    <Badge size="large" appearance="filled">{totalForDept}</Badge>
                  </div>
                  <div className={styles.statusBreakdown}>
                    {Object.entries(statusCounts).map(([status, count]) => (
                      <div key={status} className={styles.statusRow}>
                        <Text size={300}>{status}</Text>
                        <Badge appearance="outline">{count}</Badge>
                      </div>
                    ))}
                  </div>
                </Card>
              );
            })}
          </div>
        )}

        {/* Filters and Controls */}
        <Card className={styles.filtersCard}>
          <div className={styles.filtersHeader}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <FilterRegular />
              <Text weight="semibold">Filters & View</Text>
            </div>
            <Button 
              appearance="subtle" 
              onClick={() => {
                setFilter({});
                setActiveStatFilter(null);
              }}
            >
              Clear Filters
            </Button>
          </div>
          
          <div className={styles.filtersContent}>
            <div className={styles.filterGroup}>
              <Label htmlFor="groupBy">Group By</Label>
              <Select
                id="groupBy"
                value={quickFilter === 'by-department' ? 'businessUnit' : groupBy}
                onChange={(e, data) => setGroupBy(data.value as any)}
                disabled={quickFilter === 'by-department'}
              >
                <option value="none">No Grouping</option>
                <option value="status">Group by Status</option>
                <option value="businessUnit">Group by Department</option>
                <option value="assignedTo">Group by Assigned To</option>
              </Select>
            </div>

            <div className={styles.filterGroup}>
              <Label htmlFor="viewMode">View Mode</Label>
              <Select
                id="viewMode"
                value={viewMode}
                onChange={(e, data) => setViewMode(data.value as any)}
              >
                <option value="list">List View</option>
                <option value="kanban">Kanban View</option>
              </Select>
            </div>

            <div className={styles.filterGroup}>
              <Label htmlFor="statusFilter">Status</Label>
              <Select
                id="statusFilter"
                value={filter.status?.[0] || ''}
                onChange={(e, data) => setFilter(prev => ({ 
                  ...prev, 
                  status: data.value ? [data.value as TaskStatus] : undefined 
                }))}
              >
                <option value="">All Statuses</option>
                {Object.values(TaskStatus).map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </Select>
            </div>

            <div className={styles.filterGroup}>
              <Label htmlFor="businessUnitFilter">Department</Label>
              <Select
                id="businessUnitFilter"
                value={filter.businessUnit?.[0] || ''}
                onChange={(e, data) => setFilter(prev => ({ 
                  ...prev, 
                  businessUnit: data.value ? [data.value as BusinessUnit] : undefined 
                }))}
              >
                <option value="">All Departments</option>
                {Object.values(BusinessUnit).map(unit => (
                  <option key={unit} value={unit}>{unit}</option>
                ))}
              </Select>
            </div>

            <div className={styles.filterGroup}>
              <Label htmlFor="urgentFilter">Priority</Label>
              <Select
                id="urgentFilter"
                value={filter.urgent === undefined ? '' : filter.urgent.toString()}
                onChange={(e, data) => setFilter(prev => ({ 
                  ...prev, 
                  urgent: data.value === '' ? undefined : data.value === 'true' 
                }))}
              >
                <option value="">All Priorities</option>
                <option value="true">Urgent Only</option>
                <option value="false">Non-Urgent Only</option>
              </Select>
            </div>

            <div className={styles.filterGroup}>
              <Label htmlFor="searchFilter">Search</Label>
              <Input
                id="searchFilter"
                placeholder="Search tasks..."
                contentBefore={<SearchRegular />}
                onChange={(e, data) => {
                  const searchTerm = data.value.toLowerCase();
                  setFilteredTasks(tasks.filter(task => 
                    task.title.toLowerCase().includes(searchTerm) ||
                    task.description.toLowerCase().includes(searchTerm) ||
                    task.assignedTo.toLowerCase().includes(searchTerm)
                  ));
                }}
              />
            </div>
          </div>
        </Card>

        {/* Task Table */}
        {quickFilter !== 'by-department' && (
          <TaskTable
            tasks={getGroupedTasks()}
            onTaskClick={handleTaskClick}
            onUpdateTask={handleUpdateTask}
            onDeleteTask={handleDeleteTask}
            userRole={userRole}
            userId={userId}
            viewMode={viewMode}
          />
        )}

        {/* Show message if filtered tasks is empty but tasks exist */}
        {hasNoTasks && tasks.length > 0 && (
          <div className={styles.emptyState}>
            <div className={styles.emptyStateIcon}>🔍</div>
            <Title3 style={{ marginBottom: '16px' }}>No tasks match your filters</Title3>
            <Text style={{ marginBottom: '32px', fontSize: tokens.fontSizeBase400 }}>
              Try adjusting your filters or view selection.
            </Text>
            <Button 
              appearance="subtle" 
              onClick={() => {
                setFilter({});
                setActiveStatFilter(null);
                setQuickFilter('all-tasks');
              }}
            >
              Clear All Filters
            </Button>
          </div>
        )}
      </div>

      {/* Task Form Modal */}
      {showTaskForm && (
        <TaskForm
          onSubmit={handleCreateTask}
          onCancel={() => setShowTaskForm(false)}
          userRole={userRole}
          userId={userId}
          userName={userName}
        />
      )}

      {/* Task Details Modal */}
      {showTaskModal && selectedTask && (
        <TaskDetailsModal
          task={selectedTask}
          onClose={() => {
            setShowTaskModal(false);
            setSelectedTask(null);
          }}
          onUpdate={handleUpdateTask}
          onDelete={handleDeleteTask}
          userRole={userRole}
          userId={userId}
        />
      )}
    </div>
  );
};

export default TaskDashboard;
